import React, { useEffect } from "react";
import { gsap } from "gsap";
import { useNavigate } from "react-router-dom";

// Images
import SNOW_FLAKE_IMG from "../../images/snowflake.png";

// Material-UI Components
import { Box, Typography } from "@mui/material";

// Material-UI Icons
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";

function LandingPage() {
  const navigate = useNavigate();

  useEffect(() => {
    // GSAP timeline for flipping and rotating the element
    const tl = gsap.timeline();

    tl.fromTo(
      ".coedit",
      { scaleY: -1, transformOrigin: "bottom bottom" }, // Initially flipped upside down at the center
      { scaleY: 1, duration: 1, ease: "power2.out" } // Animate to the original position
    )
      .to(".coedit", {
        rotateY: 180, // Rotate 90 degrees on Y-axis from the middle
        duration: 1,
        ease: "power2.inOut",
        transformOrigin: "center center", // Ensure it rotates from the center
      })
      .to(".coedit", {
        rotateY: 0, // Rotate back to the original position on Y-axis
        duration: 1,
        ease: "power2.inOut",
        transformOrigin: "center center", // Keep the center as the rotation point
      });
  }, []);

  useEffect(() => {
    // GSAP animation for rotating the element around the Y-axis from the middle
    gsap.fromTo(
      ".oedit",
      {
        rotateY: 360, // Start rotated 90 degrees along Y-axis
        transformOrigin: "center center", // Rotate from the center of the element
        transformStyle: "preserve-3d", // Enable 3D transform for correct rendering
      },
      {
        rotateY: 0, // Rotate back to the normal position (0 degrees)
        duration: 3, // Duration of the rotation animation
        ease: "elastic.out",
      }
    );
  }, []);

  useEffect(() => {
    // GSAP animation for "B" flipping and appearing in stages (25%, 50%, 75%, 100%)

    const tl = gsap.timeline();
    tl.fromTo(
      ".edit",
      {
        rotateY: 180, // Start rotated 90 degrees along Y-axis
        y: -100, // Start below its normal position
      },
      {
        y: 0, // Rise to the normal position
        rotateY: 180, // Start rotated 90 degrees along Y-axis
        duration: 2,
        ease: "elastic.inOut",
      }
    ).to(".edit", {
      rotateY: 0, // Rotate back to the normal position (0 degrees)
      duration: 2,
      ease: "elastic.inOut",
    });
  }, []);

  useEffect(() => {
    // GSAP animation for "B" flipping and appearing in stages (25%, 50%, 75%, 100%)
    gsap.fromTo(
      ".dit",
      {
        y: -100, // Start below its normal position
      },
      {
        y: 0, // Rise to the normal position
        duration: 2,
        ease: "bounce.out",
        //
      }
    );
  }, []);

  useEffect(() => {
    // Function to handle mouse movement
    const handleMouseMove = (event) => {
      const elements = document.querySelectorAll(".moving-object");
      elements.forEach((element) => {
        const rect = element.getBoundingClientRect();
        const offsetX = (event.clientX - (rect.left + rect.width / 2)) / 50; // Minor horizontal movement
        const offsetY = (event.clientY - (rect.top + rect.height / 2)) / 50; // Minor vertical movement

        // Apply GSAP animation for smooth movement
        gsap.to(element, {
          x: offsetX,
          y: offsetY,
          duration: 0.3,
          ease: "power2.out",
        });
      });
    };

    // Attach the mousemove event listener
    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      // Cleanup the event listener on unmount
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  return (
    <>
      <Box
        sx={{
          overflow: "hidden",
          minHeight: "92vh",
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: {
            xs: "center",
            sm: "center",
            md: "center",
            lg: "flex-start",
            xl: "flex-start",
          },
          fontSize: { xs: "30vw", sm: "30vw", md: "26vw", lg: 240, xl: 300 },
          fontWeight: "bold",
          color: {
            // xs: "#023047",
            // sm: "#023047",
            xs: "white",
            sm: "white",
            md: "white",
            lg: "white",
            xl: "white",
          },
          // backgroundColor: "#023047",
          position: "relative",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mx: { xs: 0, sm: 0, md: 0, lg: 10, xl: 10 },
          }}
        >
          <div className="coedit">M</div>
          <div className="oedit">S</div>
          <div className="edit">T</div>
          <div className="dit">C</div>
        </Box>
        <Box
          id="register-btn"
          onClick={() => navigate("/events/face-off")}
          sx={{
            cursor: "pointer",
            zIndex: 1,
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            width: "fit-content",
            fontWeight: "bold",
            py: { xs: "1.5vw", sm: "1.5vw", md: "1vw", lg: 1, xl: 1 },
            px: 4,
            mx: { xs: 1, sm: 1, md: 1, lg: 10, xl: 10 },
            borderRadius: 16,
            color: "black",
            borderColor: "white",
            bgcolor: "white",
            boxShadow:
              "0 0 5px rgba(255, 255, 255, 0.4), 0 0 10px rgba(255, 255, 255, 0.2), 0 0 15px rgba(255, 255, 255, 0.3)",
            transition: "all 0.5s",
            "&:hover #register-btn-icon": {
              transform: "rotate(360deg)",
              transition: "transform 0.5s ease-in-out",
            },
            "&:hover": {
              transform: "scale(1.01)",
              transition: "transform 0.5s ease-in-out",
            },
          }}
        >
          <Typography
            fontWeight="bold"
            sx={{
              fontSize: { xs: "4.5vw", sm: "3.5vw", md: "3vw", lg: 30, xl: 30 },
              color: "#023047", // Text color
              textShadow:
                "0 0 10px rgba(2, 48, 71, 0.1), 0 0 20px rgba(2, 48, 71, 0.1), 0 0 30px rgba(2, 48, 71, 0.05)", // Lighter glow
            }}
          >
            Register to Face Off
          </Typography>
          <ArrowOutwardRoundedIcon
            id="register-btn-icon"
            sx={{
              fontWeight: "bold",
              fontSize: { xs: 30, sm: 30, md: 40, lg: 40, xl: 40 },
              ml: 2,
              transition: "transform 0.5s ease-in-out", // Initial transition setup
              color: "#023047", // Icon color
            }}
          />
        </Box>
        <div
          className="moving-object"
          style={{
            position: "absolute",
            fontSize: "3rem",
            top: "6%",
            left: "6%",
          }}
        >
          <img
            src={SNOW_FLAKE_IMG}
            alt="SNOW_FLAKE_IMG"
            style={{ width: 100, transform: "rotate(45deg)" }}
          />
        </div>
        <div
          className="moving-object"
          style={{
            position: "absolute",
            fontSize: "3rem",
            top: "8%",
            left: "40%",
          }}
        >
          <img
            src={SNOW_FLAKE_IMG}
            alt="SNOW_FLAKE_IMG"
            style={{ width: 100, transform: "rotate(45deg)" }}
          />
        </div>
        <div
          className="moving-object"
          style={{
            position: "absolute",
            fontSize: "3rem",
            top: "70%",
            left: "70%",
          }}
        >
          <img
            src={SNOW_FLAKE_IMG}
            alt="SNOW_FLAKE_IMG"
            style={{ width: 100, transform: "rotate(45deg)" }}
          />
        </div>
        <div
          className="moving-object"
          style={{
            position: "absolute",
            fontSize: "3rem",
            top: "60%",
            left: "1%",
          }}
        >
          <img
            src={SNOW_FLAKE_IMG}
            alt="SNOW_FLAKE_IMG"
            style={{ width: 100, transform: "rotate(45deg)" }}
          />
        </div>
        <div
          className="moving-object"
          style={{
            position: "absolute",
            fontSize: "3rem",
            top: "11%",
            left: "82%",
          }}
        >
          <img
            src={SNOW_FLAKE_IMG}
            alt="SNOW_FLAKE_IMG"
            style={{ width: 100, transform: "rotate(45deg)" }}
          />
        </div>
        <div
          className="moving-object"
          style={{
            position: "absolute",
            fontSize: "3rem",
            top: "80%",
            left: "20%",
          }}
        >
          <img
            src={SNOW_FLAKE_IMG}
            alt="SNOW_FLAKE_IMG"
            style={{ width: 100, transform: "rotate(45deg)" }}
          />
        </div>
      </Box>
    </>
  );
}

export default LandingPage;
